import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import { FormikDatePicker } from 'globalcomponents/UI/Form/Base';
import CalendarToday from '@material-ui/icons/CalendarToday';

import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';
import { hasRestrictedRole, getDateLocale } from 'globalutils/fmUtils';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));

const FieldWrapper = ({ label, children, errorMessage }) => (
  <FormStyle.default.FieldsWrapper className="csafield vertical">
    <Label variant="body1">{label}</Label>
    <FormStyle.default.TextWrapper>
      {children}
      {errorMessage && <Message type="error" message={errorMessage} />}
    </FormStyle.default.TextWrapper>
  </FormStyle.default.FieldsWrapper>
);

const RenderComponent = ({
  values,
  handleEdit,
  validateForm,
  customProps,
  submitAction,
  cancelAction
}) => {
  const { i18n } = useTranslation();

  const handleDateChange = (event, field) => {
    customProps.setDateValidationError('');
    handleEdit(event, { field, type: 'date' });
  };

  const handleSubmit = () => {
    validateForm().then((data) => {
      submitAction(data);
    });
  };

  return (
    <>
      {!hasRestrictedRole(['DRIVER']) && (
        <FieldWrapper
          label={i18n.t('inspections.user')}
          errorMessage={customProps.validationError}
        >
          <Field
            name="userId"
            onChange={(event) => handleEdit(event, { field: 'userId' })}
            as={Select}
            placeholder={
              window.$environment.SERVICE_PROVIDER === 'spireon'
                ? 'Select User'
                : i18n.t('common.pleaseSelect')
            }
            value={values.userId}
            suggestions={customProps.userList}
            isClearable
          />
        </FieldWrapper>
      )}
      <FieldWrapper label={i18n.t('inspections.vehicle')}>
        <Field
          name="vehicleId"
          onChange={(event) => handleEdit(event, { field: 'vehicleId' })}
          as={Select}
          placeholder={
            window.$environment.SERVICE_PROVIDER === 'spireon'
              ? 'Select Asset'
              : i18n.t('common.pleaseSelect')
          }
          value={values.vehicleId}
          suggestions={customProps.vehicleList}
          isClearable
        />
      </FieldWrapper>
      <FieldWrapper
        label={i18n.t('common.dateFrom')}
        errorMessage={customProps.dateValidationError}
      >
        <Field
          id="startDate"
          name="startDate"
          onChange={(event) => handleDateChange(event, 'startDate')}
          as={FormikDatePicker}
          locale={getDateLocale()}
          placeholderText={UI_DATE_LOWER_FORMAT}
          value={values.startDate}
          showPopperArrow={false}
          maxDate={new Date()}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          isClearable
        />
        {window.$environment.CURRENT_LAYOUT === 'SpireonNew' && <CalendarToday />}
      </FieldWrapper>
      <FieldWrapper
        label={i18n.t('common.dateTo')}
        errorMessage={customProps.dateValidationError}
      >
        <Field
          id="endDate"
          name="endDate"
          onChange={(event) => handleDateChange(event, 'endDate')}
          as={FormikDatePicker}
          locale={getDateLocale()}
          placeholderText={UI_DATE_LOWER_FORMAT}
          value={values.endDate}
          showPopperArrow={false}
          maxDate={new Date()}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          isClearable
        />
        {window.$environment.CURRENT_LAYOUT === 'SpireonNew' && <CalendarToday />}
      </FieldWrapper>
      <FormStyle.default.ButtonsWrapper className="vertical-button">
        <Button type="submit" label={i18n.t('common.view')} onClick={handleSubmit} />
        <Button type="reset" label={i18n.t('common.clear')} onClick={cancelAction} />
      </FormStyle.default.ButtonsWrapper>
    </>
  );
};

FieldWrapper.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  errorMessage: PropTypes.string
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
  cancelAction: PropTypes.func.isRequired,
  customProps: PropTypes.shape({
    userList: PropTypes.array,
    vehicleList: PropTypes.array,
    validationError: PropTypes.string,
    dateValidationError: PropTypes.string,
    setDateValidationError: PropTypes.func
  }).isRequired
};

export default RenderComponent;